import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IconChevronDown } from '@tabler/icons-react';
import { cn } from '@/utils/cn';
import TeeBadge, { type BadgeType } from '@/components/ui/tee-badge';
import TeeCheckbox from '@/components/ui/tee-checkbox';

export interface TeeDropDownListOption {
  label: string;
  value: string | number;
  disabled?: boolean;
  disabledTitle?: string;
  badge?: { label: string; type: BadgeType };
}

export interface TeeDropDownListProps {
  options: TeeDropDownListOption[];
  value?: string | number;
  onChange?: (value: string | number) => void;
  placeholder?: string;
  label?: string;
  hint?: string;
  className?: string;
  disabled?: boolean;
  /** Phase 408: opt-in multi-select. Single-select behavior is unchanged when omitted. */
  multiple?: boolean;
  values?: Array<string | number>;
  onChangeMultiple?: (values: Array<string | number>) => void;
  multipleSummary?: (count: number) => string;
}

export const TeeDropDownList = React.forwardRef<
  HTMLDivElement,
  TeeDropDownListProps
>(
  (
    {
      options,
      value,
      onChange,
      placeholder = 'Select',
      label,
      hint,
      className,
      disabled = false,
      multiple = false,
      values = [],
      onChangeMultiple,
      multipleSummary,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [menuPosition, setMenuPosition] = useState({
      top: 0,
      left: 0,
      width: 0,
    });
    const searchTimeoutRef = useRef<NodeJS.Timeout>();

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
          document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen) {
        setSearchTerm('');
        setHighlightedIndex(0);
        return;
      }
      // Open onto the current value, not the top of the list. With a dozen-plus
      // options the selected one sits below the 300px fold, so the menu opened
      // looking as though it had forgotten the selection.
      const selectedIndex = options.findIndex((opt) =>
        multiple ? values.includes(opt.value) : opt.value === value
      );
      if (selectedIndex > 0) setHighlightedIndex(selectedIndex);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Keeps the highlighted option in view — both on open and while arrowing
    // through a list taller than the menu.
    useEffect(() => {
      if (!isOpen) return;
      const node = menuRef.current?.children[highlightedIndex] as
        | HTMLElement
        | undefined;
      node?.scrollIntoView?.({ block: 'nearest' });
    }, [isOpen, highlightedIndex]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;

      if (!isOpen) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          setIsOpen(true);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < options.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Enter':
          e.preventDefault();
          handleSelectOption(options[highlightedIndex].value);
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          break;
        default:
          // Type to search
          if (e.key.length === 1) {
            e.preventDefault();
            const newSearchTerm = searchTerm + e.key.toLowerCase();
            setSearchTerm(newSearchTerm);

            // Clear previous timeout
            if (searchTimeoutRef.current) {
              clearTimeout(searchTimeoutRef.current);
            }

            // Find matching option
            const matchingIndex = options.findIndex((opt) =>
              opt.label.toLowerCase().startsWith(newSearchTerm)
            );

            if (matchingIndex !== -1) {
              setHighlightedIndex(matchingIndex);
            }

            // Reset search term after 500ms
            searchTimeoutRef.current = setTimeout(() => {
              setSearchTerm('');
            }, 500);
          }
          break;
      }
    };

    /* A modal Radix sheet locks scrolling with a document-level wheel/touch
       listener that cancels any scroll from outside its content — and this
       menu is portalled outside it. Stopping the events at the menu keeps the
       lock from seeing them, so the list scrolls past the first screenful. */
    useEffect(() => {
      const menu = menuRef.current;
      if (!isOpen || !menu) return;
      const keep = (event: Event) => event.stopPropagation();
      menu.addEventListener('wheel', keep);
      menu.addEventListener('touchmove', keep);
      return () => {
        menu.removeEventListener('wheel', keep);
        menu.removeEventListener('touchmove', keep);
      };
    }, [isOpen]);

    const handleButtonClick = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        // Menu height is capped at 300px by max-h; assume the worst case so the
        // flip decision does not depend on measuring a node that is not
        // mounted yet.
        const maxMenuHeight = Math.min(300, options.length * 44 + 8);
        const below = window.innerHeight - rect.bottom - 8;
        const flip = below < maxMenuHeight && rect.top > below;

        setMenuPosition({
          // Opening near the bottom of the viewport used to push the menu off
          // screen entirely, which read as "the dropdown does nothing".
          top: flip
            ? Math.max(8, rect.top - Math.min(maxMenuHeight, rect.top - 8) - 8)
            : rect.bottom + 8,
          left: Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8)),
          width: rect.width,
        });
      }
      setIsOpen(!isOpen);
    };

    const handleSelectOption = (optionValue: string | number) => {
      if (multiple) {
        onChangeMultiple?.(
          values.includes(optionValue)
            ? values.filter((v) => v !== optionValue)
            : [...values, optionValue]
        );
        return;
      }
      onChange?.(optionValue);
      setIsOpen(false);
    };

    const selectedOption = multiple
      ? undefined
      : options.find((opt) => opt.value === value);
    let displayLabel = selectedOption?.label || placeholder;
    if (multiple) {
      if (values.length === 1) {
        displayLabel =
          options.find((opt) => opt.value === values[0])?.label || placeholder;
      } else if (values.length > 1) {
        displayLabel =
          multipleSummary?.(values.length) ?? `${values.length} selected`;
      }
    }

    const labelClasses = cn(
      'font-[family-name:var(--type-font-family-body)]',
      'font-medium',
      'text-body-s',
      'text-neutral-700'
    );

    const hintClasses = cn(
      'font-[family-name:var(--type-font-family-body)]',
      'font-medium',
      'text-body-xs',
      'text-neutral-600'
    );

    return (
      <div className="flex w-full flex-col gap-1.5">
        {label && <label className={labelClasses}>{label}</label>}

        <div ref={ref} className={cn('relative', className)}>
          <button
            type="button"
            ref={buttonRef}
            onClick={handleButtonClick}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className={cn(
              'w-full flex items-center justify-between px-[var(--spacing-padding-margin-s,16px)] py-[var(--spacing-padding-margin-4xs,8px)] rounded-[var(--spacing-border-radius-s,8px)] border border-[var(--border-default)] bg-[var(--background-page-content)] text-left text-body-s text-[var(--text-content-title)] transition-colors',
              'hover:bg-[var(--background-card-hover)] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]',
              disabled &&
                'bg-[var(--background-card-elevated)] text-[var(--text-Label-disabled,#999)] cursor-not-allowed'
            )}
          >
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <span className="min-w-0 flex-1 truncate">{displayLabel}</span>
              {selectedOption?.badge && (
                <TeeBadge
                  size="s"
                  type={selectedOption.badge.type}
                  label={selectedOption.badge.label}
                />
              )}
            </span>
            <IconChevronDown
              size={16}
              className={cn(
                'text-[var(--text-content-sub-title,#6C756D)] transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          </button>

          {isOpen &&
            !disabled &&
            createPortal(
              <div
                ref={menuRef}
                role={multiple ? 'listbox' : undefined}
                aria-multiselectable={multiple ? true : undefined}
                // z-[9000], not z-50: Sheet is z-[70] and creates a stacking
                // context, so a dialog rendered inside a sheet paints above
                // anything at z-50 — including this menu, which is portalled to
                // document.body. A dropdown must always sit above whatever
                // contains its trigger.
                // pointer-events-auto: a modal Radix sheet sets
                // `pointer-events: none` on <body>, which this portal would
                // otherwise inherit, leaving every option unclickable.
                className="pointer-events-auto fixed bg-[var(--background-surface-floating)] rounded-[var(--radius-md,12px)] shadow-[0px_8px_32px_0px_rgba(0,0,0,0.5)] z-[9000] py-[var(--spacing-padding-margin-3xs,4px)] overflow-y-auto max-h-[300px]"
                style={{
                  top: `${menuPosition.top}px`,
                  left: `${menuPosition.left}px`,
                  width: `${menuPosition.width}px`,
                }}
              >
                {options.map((option, index) => (
                  <button
                    type="button"
                    key={option.value}
                    role={multiple ? 'option' : undefined}
                    aria-selected={multiple ? values.includes(option.value) : undefined}
                    onClick={() => !option.disabled && handleSelectOption(option.value)}
                    title={
                      option.disabled && option.disabledTitle
                        ? option.disabledTitle
                        : option.label
                    }
                    className={cn(
                      // `text-left` is load-bearing: a button centres its text by
                      // default, and a label long enough to wrap becomes a
                      // full-width anonymous flex item, so the wrapped lines
                      // centred while every short label stayed flush left.
                      'w-full flex items-center gap-2 text-left px-[var(--spacing-padding-margin-s,16px)] py-[var(--spacing-padding-margin-4xs,8px)] text-body-s text-[var(--text-content-title)] transition-colors',
                      'hover:bg-[var(--background-card-hover)] focus:outline-none',
                      (multiple ? values.includes(option.value) : value === option.value) &&
                        'bg-[var(--background-card-elevated)]',
                      highlightedIndex === index && 'bg-[var(--background-card-hover)]',
                      option.disabled && 'opacity-45 cursor-not-allowed'
                    )}
                  >
                    {multiple && (
                      <span className="pointer-events-none shrink-0">
                        <TeeCheckbox
                          size="s"
                          checked={values.includes(option.value)}
                          onChange={() => {}}
                        />
                      </span>
                    )}
                    {/* Ellipsis rather than wrap, matching the trigger above. */}
                    <span className="min-w-0 flex-1 truncate">{option.label}</span>
                    {option.badge && (
                      <span className="shrink-0">
                        <TeeBadge
                          size="s"
                          type={option.badge.type}
                          label={option.badge.label}
                        />
                      </span>
                    )}
                  </button>
                ))}
              </div>,
              document.body
            )}
        </div>

        {hint && (
          <div className="flex gap-1 items-start">
            <div className="flex shrink-0 items-center mt-0.5">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className="text-neutral-600"
              >
                <circle
                  cx="8"
                  cy="8"
                  r="7"
                  stroke="currentColor"
                  strokeWidth="1"
                />
                <text
                  x="8"
                  y="10"
                  textAnchor="middle"
                  fontSize="10"
                  fill="currentColor"
                  fontWeight="bold"
                >
                  i
                </text>
              </svg>
            </div>
            <p className={hintClasses}>{hint}</p>
          </div>
        )}
      </div>
    );
  }
);

TeeDropDownList.displayName = 'TeeDropDownList';

export default TeeDropDownList;
